import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  BookOpen, 
  Star, 
  TrendingUp, 
  Calendar, 
  Award,
  BarChart3,
  Users,
  FileText,
  Zap,
  Sparkles,
  Heart,
  TrendingDown
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useReadingGoals } from '@/hooks/useReadingGoals';
import { ReadingProgressBookshelf } from '@/components/ReadingProgressBookshelf';
import { useUserAchievements } from '@/hooks/useAchievements';
import { AchievementBadge } from '@/components/AchievementBadge';
import { fetchWrappedStats, type WrappedStats } from '@/lib/wrappedUtils';
import { RatingJourneyChart } from '@/components/wrapped/RatingJourneyChart';
import { WrappedBookCard } from '@/components/wrapped/WrappedBookCard';
import { ReadingFormatsChart } from '@/components/wrapped/ReadingFormatsChart';
import { ReadingSpeedCard } from '@/components/analytics/ReadingSpeedCard';
import { GenreSpeedComparison } from '@/components/analytics/GenreSpeedComparison';
import { AuthorSpeedInsights } from '@/components/analytics/AuthorSpeedInsights';
import { GenreDashboard, type GenreStats } from '@/components/analytics/GenreDashboard';
import { MoodArcChart, type MoodDataPoint } from '@/components/analytics/MoodArcChart';
import { AuthorInsights } from '@/components/analytics/AuthorInsights';
import { BookCharacteristics } from '@/components/analytics/BookCharacteristics';
import { ComparativeStats } from '@/components/analytics/ComparativeStats';
import { TBRAnalytics } from '@/components/analytics/TBRAnalytics';
import { 
  calculateReadingSpeedStats, 
  groupByGenreSpeed, 
  groupByAuthorSpeed,
  type ReadingSpeedData,
  type GenreSpeedData,
  type AuthorSpeedData 
} from '@/lib/analyticsUtils';
import { calculateGenreStats, calculateMoodJourney } from '@/lib/genreAnalyticsUtils';
import { 
  calculateAuthorDiversity,
  getPageCountPreferences,
  calculatePublicationYearTrends,
  calculateComparativeStats,
  type AuthorDiversityStats,
  type PageCountPreference,
  type PublicationYearTrend,
  type ComparativeRating
} from '@/lib/authorAnalyticsUtils';
import { 
  calculateTBRAnalytics,
  type TBRAnalyticsStats 
} from '@/lib/tbrAnalyticsUtils';
import { parseISO } from 'date-fns';

export default function Wrapped() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { getGoalForYear } = useReadingGoals();
  
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [stats, setStats] = useState<WrappedStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [availableYears, setAvailableYears] = useState<string[]>([]);
  const [readingGoal, setReadingGoal] = useState<any>(null);
  const [readingSpeedData, setReadingSpeedData] = useState<ReadingSpeedData | null>(null);
  const [genreSpeedData, setGenreSpeedData] = useState<GenreSpeedData[]>([]);
  const [authorSpeedData, setAuthorSpeedData] = useState<AuthorSpeedData[]>([]);
  const [genreStats, setGenreStats] = useState<GenreStats[]>([]);
  const [moodData, setMoodData] = useState<MoodDataPoint[]>([]);
  const [authorDiversity, setAuthorDiversity] = useState<AuthorDiversityStats | null>(null);
  const [pageCountData, setPageCountData] = useState<PageCountPreference[]>([]);
  const [yearTrends, setYearTrends] = useState<PublicationYearTrend[]>([]);
  const [comparativeData, setComparativeData] = useState<ComparativeRating[]>([]);
  const [tbrAnalytics, setTbrAnalytics] = useState<TBRAnalyticsStats | null>(null);
  const { data: userAchievements } = useUserAchievements(user?.id);

  useEffect(() => {
    if (user) {
      loadAvailableYears();
    }
  }, [user]);

  useEffect(() => {
    if (user && selectedYear) {
      loadYearStats(parseInt(selectedYear));
      loadReadingGoal(parseInt(selectedYear));
    }
  }, [selectedYear, user]);

  const loadReadingGoal = async (year: number) => {
    const goal = await getGoalForYear(year);
    setReadingGoal(goal);
  };

  const loadAvailableYears = async () => {
    if (!user) return;

    try {
      // Get years from finished_at dates for books on 'read' shelf
      const { data } = await supabase
        .from('user_books')
        .select('finished_at')
        .eq('user_id', user.id)
        .eq('shelf', 'read');

      const years = new Set<string>();
      
      data?.forEach(book => {
        const finishedDate = book.finished_at ? parseISO(book.finished_at) : null;
        if (finishedDate) {
          years.add(finishedDate.getFullYear().toString());
        }
      });

      const yearArray = Array.from(years).sort((a, b) => parseInt(b) - parseInt(a));
      setAvailableYears(yearArray);
      
      // If current year isn't in the list but we have data, use the most recent year
      if (yearArray.length > 0 && !yearArray.includes(selectedYear)) {
        setSelectedYear(yearArray[0]);
      }
    } catch (error) {
      console.error('Error loading available years:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Could not load available years for your reading data.",
      });
    }
  };

  const loadYearStats = async (year: number) => {
    if (!user) return;

    try {
      setLoading(true);
      const wrappedStats = await fetchWrappedStats(user.id, year);
      setStats(wrappedStats);

      // Fetch books with dates for analytics
      const { data: userBooks } = await supabase
        .from('user_books')
        .select(`
          id,
          book_id,
          started_at,
          finished_at,
          rating_star,
          shelf,
          created_at,
          tbr_category,
          books!user_books_book_id_fkey (
            id,
            title,
            authors,
            page_count,
            subjects,
            published_year,
            average_rating,
            rating_count,
            cover_url,
            cover_cache_url
          )
        `)
        .eq('user_id', user.id)
        .eq('shelf', 'read')
        .not('finished_at', 'is', null);

      // Filter to the specific year
      const yearBooks = userBooks?.filter(book => {
        const finishedDate = book.finished_at ? new Date(book.finished_at) : null;
        return finishedDate && finishedDate.getFullYear() === year;
      }) || [];

      // Calculate analytics
      if (yearBooks.length > 0) {
        // Phase 1: Reading Speed
        const booksWithDates = yearBooks.filter(b => b.started_at && b.finished_at);
        if (booksWithDates.length > 0) {
          const speedStats = calculateReadingSpeedStats(booksWithDates);
          const genreSpeedStats = groupByGenreSpeed(booksWithDates);
          const authorStats = groupByAuthorSpeed(booksWithDates);

          setReadingSpeedData(speedStats);
          setGenreSpeedData(genreSpeedStats);
          setAuthorSpeedData(authorStats);
        }

        // Phase 2: Genre Intelligence
        const genreStatistics = calculateGenreStats(yearBooks);
        const moodJourney = calculateMoodJourney(yearBooks);

        setGenreStats(genreStatistics);
        setMoodData(moodJourney);

        // Phase 3: Author & Book Characteristics
        const diversity = calculateAuthorDiversity(yearBooks);
        const pagePrefs = getPageCountPreferences(yearBooks);
        const pubYears = calculatePublicationYearTrends(yearBooks);
        const comparative = calculateComparativeStats(yearBooks);

        setAuthorDiversity(diversity);
        setPageCountData(pagePrefs);
        setYearTrends(pubYears);
        setComparativeData(comparative);

        // Phase 4: TBR Analytics
        // Fetch current TBR books
        const { data: currentTBR } = await supabase
          .from('user_books')
          .select(`
            id,
            book_id,
            created_at,
            shelf,
            tbr_category,
            books!user_books_book_id_fkey (
              id,
              title,
              authors,
              cover_url,
              cover_cache_url
            )
          `)
          .eq('user_id', user.id)
          .eq('shelf', 'tbr');

        // For historical data, get all read books from the year
        // These represent books that may have been on TBR and were read
        const tbrAnalyticsData = calculateTBRAnalytics(
          currentTBR || [],
          yearBooks
        );
        
        setTbrAnalytics(tbrAnalyticsData);
      } else {
        setReadingSpeedData(null);
        setGenreSpeedData([]);
        setAuthorSpeedData([]);
        setGenreStats([]);
        setMoodData([]);
        setAuthorDiversity(null);
        setPageCountData([]);
        setYearTrends([]);
        setComparativeData([]);
        setTbrAnalytics(null);
      }
    } catch (error) {
      console.error('Error loading year stats:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Could not load your reading stats.",
      });
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    navigate('/auth');
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      <main className="container mx-auto px-4 py-8 space-y-8">
        {/* Page Title and Year Selector */}
        <div className="text-center space-y-4">
          <h1 className="text-3xl font-heading font-bold">My Reading Wrapped</h1>
          <div className="flex items-center justify-center gap-4">
            <label className="font-medium text-muted-foreground">Year:</label>
            <Select value={selectedYear} onValueChange={setSelectedYear}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {availableYears.map(year => (
                  <SelectItem key={year} value={year}>{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-6">
                  <div className="h-4 bg-muted/40 rounded mb-4" />
                  <div className="h-8 bg-muted/20 rounded" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : !stats || stats.totalBooks === 0 ? (
          <Card className="card-elevated">
            <CardContent className="p-8 text-center">
              <BookOpen className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">No reading data for {selectedYear}</h2>
              <p className="text-muted-foreground mb-6">
                Start adding books to your reading list to see your wrapped stats!
              </p>
              <Button onClick={() => navigate('/search')}>
                Find Books to Read
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-8">
            {/* Reading Goal Progress */}
            {readingGoal && (
              <ReadingProgressBookshelf
                booksRead={stats.totalBooks}
                targetBooks={readingGoal.target_books}
                year={parseInt(selectedYear)}
              />
            )}

            {/* Main Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <Card className="card-elevated">
                <CardContent className="p-6 text-center">
                  <BookOpen className="w-8 h-8 text-primary mx-auto mb-2" />
                  <div className="text-3xl font-bold text-primary mb-1">{stats.totalBooks}</div>
                  <p className="text-muted-foreground">Books Read</p>
                </CardContent>
              </Card>

              <Card className="card-elevated">
                <CardContent className="p-6 text-center">
                  <Users className="w-8 h-8 text-secondary mx-auto mb-2" />
                  <div className="text-3xl font-bold text-secondary mb-1">{stats.uniqueAuthors}</div>
                  <p className="text-muted-foreground">Authors</p>
                </CardContent>
              </Card>

              <Card className="card-elevated">
                <CardContent className="p-6 text-center">
                  <FileText className="w-8 h-8 text-accent mx-auto mb-2" />
                  <div className="text-3xl font-bold text-accent mb-1">
                    {stats.totalPages.toLocaleString()}
                  </div>
                  <p className="text-muted-foreground">Pages Read</p>
                </CardContent>
              </Card>

              <Card className="card-elevated">
                <CardContent className="p-6 text-center">
                  <Star className="w-8 h-8 text-star mx-auto mb-2" />
                  <div className="text-3xl font-bold text-star mb-1">
                    {stats.averageRating.toFixed(1)}
                  </div>
                  <p className="text-muted-foreground">Average Rating</p>
                </CardContent>
              </Card>
            </div>

            {/* First & Last Book of the Year */}
            {(stats.firstBook || stats.lastBook) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {stats.firstBook && (
                  <WrappedBookCard
                    book={stats.firstBook}
                    title="First Book of the Year"
                    subtitle={`Started ${selectedYear} with this gem`}
                    icon={<Calendar className="w-5 h-5 text-primary" />}
                    showDate
                  />
                )}
                {stats.lastBook && (
                  <WrappedBookCard
                    book={stats.lastBook}
                    title="Last Book of the Year"
                    subtitle={`Ended ${selectedYear} with this read`}
                    icon={<Calendar className="w-5 h-5 text-secondary" />}
                    showDate
                  />
                )}
              </div>
            )}

            {/* Most & Least Popular Books */}
            {(stats.mostPopularBook || stats.leastPopularBook) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {stats.mostPopularBook && (
                  <WrappedBookCard
                    book={stats.mostPopularBook}
                    title="Most Popular Book You Read"
                    subtitle="Highest community rating"
                    icon={<TrendingUp className="w-5 h-5 text-primary" />}
                    showCommunityRating
                  />
                )}
                {stats.leastPopularBook && (
                  <WrappedBookCard
                    book={stats.leastPopularBook}
                    title="Hidden Gem"
                    subtitle="Lowest community rating - but you found it!"
                    icon={<Sparkles className="w-5 h-5 text-secondary" />}
                    showCommunityRating
                  />
                )}
              </div>
            )}

            {/* Rating Journey */}
            {stats.ratingJourney && stats.ratingJourney.length > 0 && (
              <RatingJourneyChart ratingJourney={stats.ratingJourney} />
            )}

            {/* Reading Formats - Only show if there's actual format diversity */}
            {stats.readingFormats && stats.readingFormats.length > 0 && 
             stats.readingFormats.some(f => f.format !== 'unspecified') && (
              <ReadingFormatsChart formats={stats.readingFormats} />
            )}

            {/* Reading Speed Analytics */}
            {readingSpeedData && readingSpeedData.averagePagesPerDay > 0 && (
              <ReadingSpeedCard speedData={readingSpeedData} />
            )}

            {/* Genre Speed Comparison */}
            {genreSpeedData.length > 0 && (
              <GenreSpeedComparison genreData={genreSpeedData} />
            )}

            {/* Author Speed Insights */}
            {authorSpeedData.length > 0 && (
              <AuthorSpeedInsights authorData={authorSpeedData} />
            )}

            {/* Genre Intelligence Dashboard */}
            {genreStats.length > 0 && (
              <GenreDashboard genreStats={genreStats} />
            )}

            {/* Mood Journey Chart */}
            {moodData.length > 0 && (
              <MoodArcChart moodData={moodData} />
            )}

            {/* Author Diversity Analysis */}
            {authorDiversity && authorDiversity.totalBooks > 0 && (
              <AuthorInsights diversityStats={authorDiversity} />
            )}

            {/* Book Characteristics */}
            {(pageCountData.length > 0 || yearTrends.length > 0) && (
              <BookCharacteristics 
                pageCountData={pageCountData}
                yearTrends={yearTrends}
              />
            )}

            {/* Comparative Stats */}
            {comparativeData.length > 0 && (
              <ComparativeStats comparativeData={comparativeData} />
            )}

            {/* TBR Analytics */}
            {tbrAnalytics && tbrAnalytics.totalTBRBooks + tbrAnalytics.totalReadBooks > 0 && (
              <TBRAnalytics stats={tbrAnalytics} />
            )}

            {/* Most Used Tags */}
            {stats.topTags && stats.topTags.length > 0 && (
              <Card className="card-elevated">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-primary" />
                    Your Reading Personality
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-4">
                    Your most-used tags this year
                  </p>
                  <div className="flex flex-wrap gap-3">
                    {stats.topTags.map((tag, index) => (
                      <Badge
                        key={tag.tag}
                        variant={index === 0 ? "default" : "secondary"}
                        className="px-4 py-2 text-base"
                      >
                        {tag.tag} <span className="ml-2 opacity-70">× {tag.count}</span>
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Favorite Month */}
            {stats.favoriteMonth && (
              <Card className="card-elevated">
                <CardContent className="p-6 text-center">
                  <Heart className="w-12 h-12 text-primary mx-auto mb-3" />
                  <h3 className="text-2xl font-bold mb-1">{stats.favoriteMonth.month}</h3>
                  <p className="text-muted-foreground">
                    Your most productive month - {stats.favoriteMonth.count} books!
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Longest & Shortest Books */}
            {(stats.longestBook || stats.shortestBook) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {stats.longestBook && (
                  <WrappedBookCard
                    book={stats.longestBook}
                    title="Longest Book"
                    subtitle="Your biggest reading challenge"
                    icon={<Award className="w-5 h-5 text-primary" />}
                  />
                )}
                {stats.shortestBook && (
                  <WrappedBookCard
                    book={stats.shortestBook}
                    title="Shortest Book"
                    subtitle="Quick and sweet"
                    icon={<Zap className="w-5 h-5 text-secondary" />}
                  />
                )}
              </div>
            )}

            {/* Top Genres */}
            {stats.topGenres && stats.topGenres.length > 0 && (
              <Card className="card-elevated">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-primary" />
                    Top Genres
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {stats.topGenres.map((genre) => (
                      <Badge key={genre.genre} variant="secondary" className="text-sm px-3 py-1">
                        {genre.genre} ({genre.count})
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Top Authors */}
            {stats.topAuthors.length > 0 && (
              <Card className="card-elevated">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-accent" />
                    Top Authors
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {stats.topAuthors.map((author, index) => (
                      <div key={author.author} className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary">
                            {index + 1}
                          </div>
                          <span className="font-medium">{author.author}</span>
                        </div>
                        <Badge variant="outline">{author.count} book{author.count !== 1 ? 's' : ''}</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Monthly Breakdown */}
            {stats.monthlyBreakdown.length > 0 && (
              <Card className="card-elevated">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-primary" />
                    Reading by Month
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {stats.monthlyBreakdown.map((month) => (
                      <div key={month.month} className="flex items-center justify-between">
                        <span className="text-sm font-medium">{month.month}</span>
                        <div className="flex items-center gap-2">
                          <div className="w-24 bg-muted rounded-full h-2">
                            <div 
                              className="bg-primary h-2 rounded-full" 
                              style={{ 
                                width: `${(month.count / Math.max(...stats.monthlyBreakdown.map(m => m.count))) * 100}%` 
                              }}
                            />
                          </div>
                          <span className="text-sm text-muted-foreground w-8">{month.count}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Achievements Unlocked This Year */}
            {userAchievements && userAchievements.length > 0 && (
              <Card className="card-elevated">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Award className="w-5 h-5 text-primary" />
                    Achievements Unlocked in {selectedYear}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    {userAchievements
                      .filter(ua => {
                        const unlockedYear = new Date(ua.unlocked_at).getFullYear().toString();
                        return unlockedYear === selectedYear;
                      })
                      .slice(0, 12)
                      .map((ua) => (
                        <div key={ua.id} className="flex flex-col items-center gap-2">
                          <AchievementBadge
                            achievement={ua.achievement!}
                            unlocked={true}
                            size="md"
                          />
                          <p className="text-xs text-center text-muted-foreground line-clamp-2">
                            {ua.achievement!.name}
                          </p>
                        </div>
                      ))}
                  </div>
                  {userAchievements.filter(ua => new Date(ua.unlocked_at).getFullYear().toString() === selectedYear).length > 12 && (
                    <Button variant="outline" size="sm" className="w-full mt-4" onClick={() => navigate('/achievements')}>
                      View All Achievements
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </main>
    </div>
  );
}